export type ChauffeurPage = {
  slug: string;
  title: string;
  subtitle?: string;
  keywords: string[];
  hero: {
    title: string;
    subtitle?: string;
    keywords?: string[];
    imageSrc?: string;
    imageAlt?: string;
    badges?: string[];
  };
  benefits: {
    heading: string;
    items: { title: string; description: string }[];
  };
  services: { heading: string; services: { title: string; text: string }[] };
  locations: {
    heading: string;
    items: { name: string; description?: string }[];
  };
  generic?: {
    heading: string;
    subheading?: string;
    paragraphs?: string[];
    bullets?: string[];
    imageSrc?: string;
    imageAlt?: string;
  };
  process?: {
    heading: string;
    steps: string[];
  };
  faq?: {
    q: string;
    a: string;
  }[];
  relatedLinks?: {
    label: string;
    href: string;
  }[];
  cta: {
    title: string;
    text: string;
    primaryHref: string;
    primaryLabel: string;
    secondaryHref?: string;
    secondaryLabel?: string;
  };
  seo: {
    title: string;
    description: string;
    canonical?: string;
  };
};

import services from "./slugs/services";
import cannes from "./slugs/cannes";
import coteDAzur from "./slugs/cote-d-azur";
import evenements from "./slugs/evenements";
import entreprise from "./slugs/entreprise";
import aeroport from "./slugs/aeroport";
import nice from "./slugs/nice";
import monaco from "./slugs/monaco";
import antibes from "./slugs/antibes";
import saintTropez from "./slugs/saint-tropez";
import festivalDeCannes from "./slugs/festival-de-cannes";
import mipim from "./slugs/mipim";
import mipcom from "./slugs/mipcom";
import cannesLions from "./slugs/cannes-lions";
import grandPrixMonaco from "./slugs/grand-prix-de-monaco";
import monacoYachtShow from "./slugs/monaco-yacht-show";
import saintJeanCapFerrat from "./slugs/saint-jean-cap-ferrat";
import eze from "./slugs/eze";
import villefranche from "./slugs/villefranche-sur-mer";
import mougins from "./slugs/mougins";
import sophiaAntipolis from "./slugs/sophia-antipolis";
import cannesYachtingFestival from "./slugs/cannes-yachting-festival";
import tfwaCannes from "./slugs/tfwa-cannes";
import monteCarloMasters from "./slugs/monte-carlo-masters";
import iltmCannes from "./slugs/iltm-cannes";
import carnavalDeNice from "./slugs/carnaval-de-nice";
import chauffeurSecurite from "./slugs/chauffeur-securite";
import minibusVip from "./slugs/minibus-vip";
import berlineAvecChauffeur from "./slugs/berline-avec-chauffeur";
import classeSAvecChauffeur from "./slugs/classe-s-avec-chauffeur";
import vanAvecChauffeur from "./slugs/van-avec-chauffeur";
import suvAvecChauffeur from "./slugs/suv-avec-chauffeur";

// Villes Riviera — expansion locality (chauffeur/VTC + transfert par commune).
import mandelieuLaNapoule from "./slugs/mandelieu-la-napoule";
import leCannet from "./slugs/le-cannet";
import vallaurisGolfeJuan from "./slugs/vallauris-golfe-juan";
import juanLesPins from "./slugs/juan-les-pins";
import theouleSurMer from "./slugs/theoule-sur-mer";
import grasse from "./slugs/grasse";
import valbonne from "./slugs/valbonne";
import biot from "./slugs/biot";
import villeneuveLoubet from "./slugs/villeneuve-loubet";
import cagnesSurMer from "./slugs/cagnes-sur-mer";
import saintLaurentDuVar from "./slugs/saint-laurent-du-var";
import saintPaulDeVence from "./slugs/saint-paul-de-vence";
import vence from "./slugs/vence";
import beaulieuSurMer from "./slugs/beaulieu-sur-mer";
import capDAil from "./slugs/cap-d-ail";
import beausoleil from "./slugs/beausoleil";
import menton from "./slugs/menton";
import roquebruneCapMartin from "./slugs/roquebrune-cap-martin";
import laTurbie from "./slugs/la-turbie";
import tourrettesSurLoup from "./slugs/tourrettes-sur-loup";
import laColleSurLoup from "./slugs/la-colle-sur-loup";
import mouansSartoux from "./slugs/mouans-sartoux";
import opio from "./slugs/opio";
import roquefortLesPins from "./slugs/roquefort-les-pins";
import saintRaphael from "./slugs/saint-raphael";
import frejus from "./slugs/frejus";
import sainteMaxime from "./slugs/sainte-maxime";
import grimaud from "./slugs/grimaud";
import portGrimaud from "./slugs/port-grimaud";
import cogolin from "./slugs/cogolin";
import gassin from "./slugs/gassin";
import laCroixValmer from "./slugs/la-croix-valmer";
import ramatuelle from "./slugs/ramatuelle";
import cavalaireSurMer from "./slugs/cavalaire-sur-mer";
import leLavandou from "./slugs/le-lavandou";
import bormesLesMimosas from "./slugs/bormes-les-mimosas";
import hyeres from "./slugs/hyeres";
import toulon from "./slugs/toulon";

// Événements Riviera (agenda — pages événement dédiées).
import niceJazzFestival from "./slugs/nice-jazz-festival";
import jazzAJuan from "./slugs/jazz-a-juan";
import lesVoilesDeSaintTropez from "./slugs/les-voiles-de-saint-tropez";
import feteDuCitronMenton from "./slugs/fete-du-citron-menton";
import regatesRoyalesCannes from "./slugs/regates-royales-cannes";
import ironmanFranceNice from "./slugs/ironman-france-nice";
import jumpingInternationalMonaco from "./slugs/jumping-international-monaco";
import monacoEPrix from "./slugs/monaco-e-prix";
import mapicCannes from "./slugs/mapic-cannes";
import canneseries from "./slugs/canneseries";
import feteDuJasminGrasse from "./slugs/fete-du-jasmin-grasse";
import giragliaSaintTropez from "./slugs/giraglia-saint-tropez";
import voitureMariageCannes from "./slugs/voiture-mariage-cannes";

export const chauffeurPages: ChauffeurPage[] = [
  services,
  cannes,
  coteDAzur,
  evenements,
  entreprise,
  aeroport,
  nice,
  monaco,
  antibes,
  saintTropez,
  festivalDeCannes,
  mipim,
  mipcom,
  cannesLions,
  grandPrixMonaco,
  monacoYachtShow,
  saintJeanCapFerrat,
  eze,
  villefranche,
  mougins,
  sophiaAntipolis,
  cannesYachtingFestival,
  tfwaCannes,
  monteCarloMasters,
  iltmCannes,
  carnavalDeNice,
  chauffeurSecurite,
  minibusVip,
  berlineAvecChauffeur,
  classeSAvecChauffeur,
  vanAvecChauffeur,
  suvAvecChauffeur,
  mandelieuLaNapoule,
  leCannet,
  vallaurisGolfeJuan,
  juanLesPins,
  theouleSurMer,
  grasse,
  valbonne,
  biot,
  villeneuveLoubet,
  cagnesSurMer,
  saintLaurentDuVar,
  saintPaulDeVence,
  vence,
  beaulieuSurMer,
  capDAil,
  beausoleil,
  menton,
  roquebruneCapMartin,
  laTurbie,
  tourrettesSurLoup,
  laColleSurLoup,
  mouansSartoux,
  opio,
  roquefortLesPins,
  saintRaphael,
  frejus,
  sainteMaxime,
  grimaud,
  portGrimaud,
  cogolin,
  gassin,
  laCroixValmer,
  ramatuelle,
  cavalaireSurMer,
  leLavandou,
  bormesLesMimosas,
  hyeres,
  toulon,
  niceJazzFestival,
  jazzAJuan,
  lesVoilesDeSaintTropez,
  feteDuCitronMenton,
  regatesRoyalesCannes,
  ironmanFranceNice,
  jumpingInternationalMonaco,
  monacoEPrix,
  mapicCannes,
  canneseries,
  feteDuJasminGrasse,
  giragliaSaintTropez,
  voitureMariageCannes,
];

export const getChauffeurPage = (slug: string) =>
  chauffeurPages.find((p) => p.slug === slug);

export const chauffeurSlugs = () => chauffeurPages.map((p) => p.slug);
